import React, { useMemo } from "react";
import { Divider } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { t } from "i18next";

import { ButtonComp, Icon } from "components";
import BluePrintIcon from "components/Icon/BluePrintIcon/index";
import FeeTransparency from "components/FeeTransparency";
import {
  getVisibleInPersonTour,
  getVisibleLiveVideoTour,
  getVisibleScheduleTour,
  getVisibleSelfGuidedTour,
  setIsUnitTourScreen,
  setReplies,
  addNewMessage,
  getAppSSID,
  selectPropertyId,
  getConversationID,
} from "slices";
import { createHayleyMessage, createItemRecord, getNextOptions, makeNextOptions } from "utils/methods/index";

import { formatUnitData } from "../utils";

export const UnitView = ({ selectedUnit, startScheduleTour }) => {
  const dispatch = useDispatch();
  const SSID = useSelector(getAppSSID);
  const propertyId = useSelector(selectPropertyId);

  const visibleScheduleTour = useSelector(getVisibleScheduleTour);
  const visibleInPersonTour = useSelector(getVisibleInPersonTour);
  const visibleLivePhotoTour = useSelector(getVisibleLiveVideoTour);
  const visibleSelfGuidedTour = useSelector(getVisibleSelfGuidedTour);
  const conversationID = useSelector(getConversationID);

  const formattedData = useMemo(() => formatUnitData(selectedUnit), [selectedUnit]);

  const record = (clickValue) => {
    createItemRecord(propertyId, SSID, clickValue);
  };

  const handleSchedule = () => {
    record("schedule a tour");
    startScheduleTour();
  };

  const go2ContactUs = () => {
    record("contact us");

    getNextOptions("Form4ContactUs")
      .then((res) => {
        const { nextReplies, nextBotMessages } = makeNextOptions("ScheduleTour", res.data, res.extraData || undefined);
        dispatch(setIsUnitTourScreen(false));
        dispatch(setReplies(nextReplies));

        createHayleyMessage({
          conversationId: conversationID || "",
          propertyId,
          text: "Contact Me",
          queryType: "question",
          ownerType: "prospect",
          appName: "HayleyChat",
          kgNodes: [{ l0_subject: "Contact Me" }],
        }).then((res) => res.json());

        dispatch(
          addNewMessage([
            {
              message: "Contact Me",
              speaker: "user",
              optionsType: "Init",
              key: "contact_us",
              createdAt: new Date().toISOString(),
            },
            ...nextBotMessages,
          ])
        );

        if (nextBotMessages?.length > 0) {
          const lastMSg = nextBotMessages[nextBotMessages.length - 1];

          if (lastMSg?.message) {
            createHayleyMessage({
              conversationId: conversationID || "",
              propertyId,
              text: lastMSg.message,
              queryType: "answer",
              ownerType: "agent",
              appName: "HayleyChat",
              kgNodes: [],
            }).then((res) => res.json());
          }
        }
      })
      .catch(() => {
        /** @todo Add error handling part */
      });
  };

  return (
    <div
      style={{ maxHeight: "calc(100% - 50px)" }}
      className="hayley-flex hayley-h-full hayley-w-full hayley-flex-col hayley-justify-between hayley-overflow-auto hayley-border hayley-border-solid hayley-border-custom-2"
    >
      <div className="hayley-relative hayley-left-3 hayley-top-1 hayley-flex hayley-w-fit  hayley-items-center hayley-gap-x-2 hayley-rounded-[30px] hayley-bg-[#F9F9F9] hayley-px-3 hayley-py-[6px]">
        <BluePrintIcon />
        <span className="hayley-text-[12px] hayley-leading-[18px] hayley-text-[#333333] ">{formattedData.name || "- -"}</span>
      </div>
      <div className="hayley-flex hayley-h-full hayley-w-full hayley-items-center hayley-justify-center hayley-overflow-hidden" style={{ background: "#FFF" }}>
        {selectedUnit?.unit_floor_plan_image?.[0]?.image_url ? (
          <img src={selectedUnit?.unit_floor_plan_image?.[0]?.image_url} className="hayley-h-full hayley-w-full hayley-object-contain" alt="" />
        ) : (
          <div className="hayley-flex hayley-flex-col hayley-items-center hayley-justify-center">
            <div className="hayley-mb-1">
              <Icon iconType={"PlaceholderImage"} />
            </div>
            <p className="hayley-text-[12px] hayley-text-[#999999]">Image coming soon...</p>
          </div>
        )}
      </div>
      <div>
        <Divider />
        <FeeTransparency
          totalPrice={selectedUnit?.total_price}
          baseRent={selectedUnit?.unit_rent_min}
          leaseTerm={selectedUnit?.external_lease_term}
          fees={selectedUnit?.fees || []}
          unitBedrooms={formattedData?.unitType}
          unitArea={formattedData?.area}
          availabilityDate={formattedData?.availabilityDate}
          hidePricing={selectedUnit?.hide_pricing}
          tmlpEnabled={selectedUnit?.tmlp_enabled}
        />

        <div className="hayley-flex hayley-flex-wrap hayley-px-4 hayley-py-3">
          {visibleScheduleTour && (visibleInPersonTour || visibleLivePhotoTour || visibleSelfGuidedTour) && selectedUnit?.is_available && (
            <ButtonComp label={t("scheduleTour")} className="hayley-mb-2 hayley-mr-2" onClick={handleSchedule} disabled={false} filled={true} />
          )}
          {selectedUnit?.unit_availability_url && selectedUnit?.is_available ? (
            <ButtonComp
              icon={"OpenLinkIcon"}
              label={t("applyNow")}
              onClick={() => window.open(selectedUnit?.unit_availability_url, "_blank")}
              disabled={false}
              isLeftIcon={false}
              className="hayley-mb-2 hayley-mr-2 !hayley-px-5 !hayley-py-[10px]"
            />
          ) : (
            <ButtonComp label={t("contactMe")} className="hayley-mb-2 hayley-mr-2" onClick={() => go2ContactUs()} disabled={false} />
          )}
        </div>
      </div>
    </div>
  );
};
